/**
 * Integration GraphQL Fragments
 *
 * Reusable fragments for Integration primitive queries and mutations.
 * Fields match backend Integration struct and GqlIntegration model 1:1.
 *
 * @see graphql_gateway/src/integration_api/integration_model.rs - Backend model
 * @see common/src/primitives/integration/mod.rs - Rust struct
 */

import { gql } from '@apollo/client';

/**
 * Complete Integration fields fragment
 *
 * Used by all queries and mutations that return Integration data.
 * tenant_id is #[graphql(skip)] on the backend.
 */
export const INTEGRATION_FIELDS = gql`
  fragment IntegrationFields on Integration {
    # Identity (7 fields, tenant_id skipped)
    integrationId
    integrationName
    integrationCode
    slug
    externalId
    description
    integrationType

    # Connection Configuration (15 fields)
    baseUrl
    protocol
    httpVersion
    timeoutMs
    maxConnections
    keepAlive
    compression
    endpoints
    customHeaders
    tlsConfig
    proxyConfig
    dnsCacheTtlSeconds
    connectTimeoutMs
    readTimeoutMs
    credentialVaultRef

    # Authentication (9 fields)
    authType
    authConfig
    authScope
    tokenRefreshEnabled
    tokenRefreshThresholdSeconds
    clientCertificateRef
    rotateCredentials
    credentialRotationDays
    lastCredentialRotation

    # Health Monitoring (11 fields)
    currentHealthStatus
    healthCheckEnabled
    healthCheckIntervalSeconds
    healthCheckEndpoint
    healthCheckMethod
    healthCheckExpectedStatus
    healthCheckTimeoutMs
    lastHealthCheck
    lastSuccessfulCall
    uptimePercentage
    avgResponseTimeMs

    # Retry & Circuit Breaker (15 fields)
    retryEnabled
    maxRetries
    retryBackoffType
    retryInitialDelayMs
    retryMaxDelayMs
    retryMultiplier
    retryOnStatusCodes
    circuitBreakerEnabled
    circuitBreakerState
    circuitBreakerThreshold
    circuitBreakerResetSeconds
    consecutiveFailures
    lastCircuitBreakerTrip
    lastCircuitBreakerReset
    circuitBreakerHalfOpenMaxCalls

    # Rate Limiting (9 fields)
    rateLimitEnabled
    rateLimitRequestsPerWindow
    rateLimitWindowSeconds
    rateLimitStrategy
    rateLimitResetAt
    currentRateLimitRemaining
    rateLimitQueueMaxSize
    rateLimitCurrentTokens
    rateLimitLastRefill

    # Webhook Configuration (7 fields)
    webhookEnabled
    webhookSecret
    webhookSignatureAlgorithm
    webhookSignatureHeader
    webhookEvents
    webhookRetryFailed
    webhookMaxPayloadSizeBytes

    # Formula Interactions (8 fields)
    eventStreamId
    metricsAnalyticsId
    alertNotificationIds
    asyncQueueId
    syncScheduleId
    managedResourceIds
    transactionId
    checkpointOnFailure

    # Lifecycle & Audit (7 fields, tenant_id skipped)
    createdBy
    createdAt
    updatedAt
    version
    metadata
    originalId
    parentId
    isActive
  }
`;

/**
 * Integration connection fields (pagination)
 */
export const INTEGRATION_CONNECTION_FIELDS = gql`
  ${INTEGRATION_FIELDS}
  fragment IntegrationConnectionFields on IntegrationConnection {
    nodes {
      ...IntegrationFields
    }
    totalCount
    hasNextPage
    endCursor
  }
`;

/**
 * Health check result fields
 */
export const HEALTH_CHECK_RESULT_FIELDS = gql`
  fragment HealthCheckResultFields on IntegrationHealthCheckResult {
    integrationId
    status
    responseTimeMs
    errorMessage
    checkedAt
  }
`;

/**
 * Connection test result fields
 */
export const CONNECTION_TEST_RESULT_FIELDS = gql`
  fragment ConnectionTestResultFields on IntegrationConnectionTestResult {
    integrationId
    success
    responseTimeMs
    statusCode
    errorMessage
    testedAt
  }
`;

/**
 * Integration utilization report fields
 */
export const INTEGRATION_UTILIZATION_REPORT_FIELDS = gql`
  fragment IntegrationUtilizationReportFields on IntegrationUtilizationReport {
    integrationId
    integrationName
    totalRequests
    averageResponseTimeMs
    uptimePercentage
    errorRate
    currentHealthStatus
    circuitBreakerTripsLast24h
    reportGeneratedAt
  }
`;
